/* eslint max-classes-per-file: "off" */
/* eslint class-methods-use-this: "off" */
import { Map as OLMap, MapBrowserEvent, Feature } from 'ol';
import { fromLonLat, transformExtent } from 'ol/proj';
import OSM from 'ol/source/OSM';
import { Extent } from 'ol/extent';
import { Geometry, MultiPoint } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import { MousePosition, ScaleLine } from 'ol/control';

import { Common } from 'models';

import { createStringXY } from 'ol/coordinate';
import View, { FitOptions } from 'ol/View';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { DragPan } from 'ol/interaction';
import { MAP_PROJECTION } from 'config';

type Bounds = Common.Bounds;

export interface MapCommand {
    /**
     * Serves as the entrypoint to a map controller.
     * When the control is dispatched from a map dispatch, it will
     * have a reference to the map passed to it, allowing it to
     * perform it's desired task or to store the reference away
     * for a later time.
     * @param map Is a reference to the map
     * @param dispatch Is a reference to the map dispatch used
     *                 to dispatch this control. This allows
     *                 controls to dispatch their own sub-controls.
     */
    apply: (map: OLMap, dispatch: MapDispatch) => void;
}

/**
 * The Map Dispatcher that accepts controls that implement
 * the MapCommand interface. The MapView control will return
 * a MapDispatch via it's `registerMapCommand` callback once
 * the map has loaded.
 * This dispatch can then be used to connect controls to the
 * corresponding map
 */
export type MapDispatch = (command: MapCommand) => void;

export interface Identifiable {
    /**
     * Used by the identify tool to know if the layer should be identified.
     * Can be used by the layer to do it's own thing.
     * If the result is false, it won't make a request.
     * If the result is 'stop', the identify tool won't make any requests.
     */
    identifiable: (event: MapBrowserEvent<UIEvent>) => boolean | 'stop';
}

/**
 * A basic control that can be passed to react components.
 * When dispatched it will contain a reference to the Map
 * that can be accessed at any time after the map has loaded.
 */
export class MapReference implements MapCommand {
    private map?: OLMap;

    apply(map: OLMap) {
        this.map = map;
    }

    getMap() {
        return this.map;
    }
}

/**
 * A control that when dispatched would immidiately
 * zoom to the provided coordinates.
 * The zoom level can optionally be provided, if not
 * provided, the same zoom level will be used.
 */
export class ZoomToCoords implements MapCommand {
    constructor(coords: number[], zoom?: number) {
        this.coords = coords;
        this.zoom = zoom;
    }

    zoom?: number;

    coords: number[];

    apply(map: OLMap) {
        map.getView().animate({
            zoom: this.zoom,
            center: fromLonLat(this.coords),
        });
    }
}

const isBounds = (bounds: Bounds | Extent): bounds is Bounds => {
    const b = bounds as Bounds;
    return b.minLat !== undefined && b.maxLong !== undefined;
};

/**
 * A map control which will zoom to an Extent or given bounds
 * immidiantely after being dispatched.
 */
export class ZoomToBounds implements MapCommand {
    constructor(bounds: Bounds | Extent, options?: FitOptions) {
        this.bounds = bounds;
        this.options = {
            padding: [50, 50, 50, 50],
            maxZoom: 20,
            duration: 1000,
            ...options,
        };
    }

    bounds: Bounds | Extent;

    options: FitOptions;

    apply(map: OLMap) {
        if (isBounds(this.bounds)) {
            const geometry = new MultiPoint([
                fromLonLat([this.bounds.minLong, this.bounds.minLat]),
                fromLonLat([this.bounds.maxLong, this.bounds.maxLat]),
            ]);

            map.getView().fit(geometry, this.options);
        } else {
            map.getView().fit(this.bounds, this.options);
        }
    }
}

export class GetMapBounds implements MapCommand {
    constructor(private cb?: (bounds: Common.Bounds) => void) {}

    private bounds: Common.Bounds | null = null;

    getBounds() {
        return this.bounds;
    }

    apply(map: OLMap) {
        map.on('moveend', (event) => {
            const extent = transformExtent(
                event.map.getView().calculateExtent(event.map.getSize()),
                MAP_PROJECTION.getCode(),
                'EPSG:4326',
            );
            this.bounds = {
                minLong: extent[0],
                minLat: extent[1],
                maxLong: extent[2],
                maxLat: extent[3],
            };
            if (this.cb) this.cb(this.bounds);
        });
    }
}

export class AddOSM implements MapCommand {
    apply(map: OLMap) {
        map.addLayer(
            new TileLayer({
                source: new OSM(),
                zIndex: -1,
            }),
        );
    }
}

export class Resize implements MapCommand {
    apply(map: OLMap) {
        map.updateSize();
    }
}

export interface CoordsWithId {
    coords: number[];
    id: number;
    meta?: string;
    text?: string;
}

export class AddScaleLine implements MapCommand {
    apply(map: OLMap) {
        const scaleLine = new ScaleLine({
            target: '',
        });

        map.addControl(scaleLine);
    }
}

export class MouseCoords implements MapCommand {
    apply(map: OLMap) {
        const mousePositionControl = new MousePosition({
            coordinateFormat: createStringXY(4),
            projection: 'EPSG:4326',
            undefinedHTML: '&nbsp;',
        });
        map.addControl(mousePositionControl);
    }
}

/**
 * Gets the view of the first map and attaches
 * that view to all other maps that are applied afterwards.
 * This allows for synchronous map views across each map.
 */
export class MapMove implements MapCommand {
    view?: View;

    maps: OLMap[] = [];

    resize() {
        this.maps.forEach((x) => x.updateSize());
    }

    apply(map: OLMap) {
        this.maps.push(map);
        if (!this.view) {
            this.view = map.getView();
            return;
        }
        map.setView(this.view);
    }
}

/**
 * A Map Control that will display an Overlay at a given position
 * immidiately after being dispatched.
 */
export class ShowOverlay implements MapCommand {
    /**
     *
     * @param id The Id of the overlay provided within the MapView's Overlays parameter
     * @param coords The coordinates as a [latitude, longitude] number list.
     * @param hideOtherOverlays Optional - Default true - If true it will hide all other overlays.
     *                          If false other open overlays will remain.
     */
    constructor(
        private id: string,
        private coords?: number[],
        private hideOtherOverlays = true,
    ) {}

    apply(map: OLMap) {
        if (this.hideOtherOverlays)
            map.getOverlays().forEach((overlay) =>
                overlay.setPosition(undefined),
            );

        const overlay = map.getOverlayById(this.id);
        if (this.coords && overlay) {
            overlay.setPosition(fromLonLat(this.coords));

            return;
        }

        // undefined is a legal and intended input that
        // the TS Def doesn't seem to recognise.
        // It acts as a way to hide an overlay
        // @ts-ignore
        overlay?.setPosition(undefined);
    }
}

/**
 * A Map Control that will hide a given overlay if visible
 */
export class HideOverlay implements MapCommand {
    /**
     *
     * @param id The Id of the overlay to hide provided within the MapView's Overlays parameter
     */
    constructor(id: string) {
        this.id = id;
    }

    private id: string;

    apply(map: OLMap) {
        const overlay = map.getOverlayById(this.id);

        // @ts-ignore
        overlay?.setPosition(undefined);
    }
}

export class StartMoveOverlay implements MapCommand {
    constructor(id: string) {
        this.id = id;
        this.dragPan = null;
    }

    private id: string;

    private dragPan: any;

    apply(map: OLMap) {
        // bit of a hacky function.
        const overlay = map.getOverlayById(this.id);

        map.getInteractions().forEach((interaction: any) => {
            if (interaction instanceof DragPan) {
                this.dragPan = interaction;
            }
        });

        this.dragPan.setActive(false);
        overlay.set('dragging', true);

        const startMoveFn = (evt: any) => {
            if (overlay.get('dragging') === true) {
                overlay.setOffset([-20, -20]);
                overlay.setPosition(evt.coordinate);
            }
        };

        const stopMoveFn = () => {
            if (overlay.get('dragging') === true) {
                this.dragPan.setActive(true);
                overlay.set('dragging', false);
            }
            // no once event
            map.removeEventListener('pointermove', startMoveFn);
            map.removeEventListener('pointermove', stopMoveFn);
        };

        map.addEventListener('pointermove', startMoveFn);

        map.addEventListener('pointerup', stopMoveFn);
    }
}

export class StopMoveOverlay implements MapCommand {
    constructor(id: string) {
        this.id = id;
    }

    private id: string;

    apply(map: OLMap) {
        // bit of a hacky function.
        const overlay = map.getOverlayById(this.id);

        overlay.setOffset([0, 0]);
    }
}

export class HighlightFeature implements MapCommand {
    constructor(
        highlightLayer: VectorLayer<VectorSource<Geometry>>,
        feature?: Feature<Geometry> | undefined,
    ) {
        this.highlightLayer = highlightLayer;
        this.feature = feature;
    }

    feature?: Feature<Geometry> | undefined;

    highlightLayer: VectorLayer<VectorSource<Geometry>>;

    apply(map: OLMap) {
        map.removeLayer(this.highlightLayer);
        // Get the source of the vector layer
        const layerSource = this.highlightLayer.getSource();
        // Add the new feature to the source
        if (this.feature != null && this.feature != undefined) {
            layerSource?.clear();
            layerSource?.addFeature(this.feature);
            map.addLayer(this.highlightLayer);
            this.highlightLayer.setZIndex(10000);
        } else {
            layerSource?.clear();
        }
    }
}
